import "../navigation.scss";

import { useAuthenticator } from "@aws-amplify/ui-react";
import { Drawer } from "@mui/material";
import classNames from "classnames";
import { getPendingImports } from "components/ImportData/utils";
import NotificationCenter from "components/NotificationCenter";
import { getLateDeclarations } from "containers/DeclarationPage/utils";
import { PortalType } from "contexts/portal.context";
import { useAbility } from "hooks/useAbility";
import { useCompanySettings } from "hooks/useCompanySettings";
import useCrisp from "hooks/useCrisp";
import { useIsMobile } from "hooks/useIsMobile";
import { useIsPWA } from "hooks/usePWAMode";
import { useContext, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { fetchImportedFiles } from "store/filesHistorySlice";
import { toggleNavDrawer, toggleNotificationsDrawer } from "store/settingsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { EPortalType } from "store/types";

import { useAuth } from "../../../../contexts/auth/AuthContext";
import { displayMobileNavBtn } from "../../BPMobileNavBtn";
import RolePreview from "../../RolePreview";
import {
    CollapseButton,
    DeclarationsLink,
    HelpMenu,
    NavigationHeader,
    NavigationLayout,
    NavigationLink,
    NavigationProfile,
    NotificationsButton,
    PendingImportsButton,
} from "../components";
import { NavigationProvider, useNavigationCollapse } from "../context";
import type { NavigationLinkConfig } from "../types";

export interface PublisherNavigationProps {
    /** Main navigation links */
    navLinks: NavigationLinkConfig[];
    /** Bottom section links */
    bottomLinks: NavigationLinkConfig[];
}

/**
 * Inner navigation component that uses the collapse context
 */
const PublisherNavigationContent = ({ navLinks, bottomLinks }: PublisherNavigationProps) => {
    const portalType = useContext(PortalType);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { isMobile } = useIsMobile();
    const isPWAMode = useIsPWA();
    const { isCollapsed } = useNavigationCollapse();
    const ability = useAbility();

    const onOpenNavDrawer = useAppSelector((state) => state.settings.showNavDrawer);
    const onOpenNotificationsDrawer = useAppSelector((state) => state.settings.showNotificationsDrawer);
    const { user, signOut, authStatus } = useAuthenticator((context) => [context.user]);
    const accountType = user.attributes?.["custom:accountType"];

    const { notifications } = useAppSelector((state) => state.notification);
    const { companySettings, userData, rolePreview } = useAppSelector((state) => state.settings);
    const { contributionList } = useAppSelector((state) => state.contribution);
    const { jobs } = useAppSelector((state) => state.jobs);
    const { pendingImports } = useAppSelector((state) => state.dataImport);

    const hasMultipleCompanies = userData?.companies && userData?.companies.length > 1;
    const isAdminProd = accountType === "admin" && window.location.host === "manage.crealo.app";
    const showCompanySelector = hasMultipleCompanies && !isAdminProd;
    const { isAdminCrealo: isCrealoAdmin } = useAuth();

    const { handleSwitchCompany } = useCompanySettings({ portalType });

    // Only use collapse on desktop
    const effectiveCollapsed = !isMobile && isCollapsed;

    // Filter links based on abilities and compute disabled state
    const allowedLinks = useMemo(() => {
        return navLinks
            .filter(({ canAccess, extraCondition = true }) => {
                // If no canAccess function is provided, link is visible by default
                const hasAccess = canAccess ? canAccess(ability) : true;
                return hasAccess && extraCondition;
            })
            .map((link) => ({
                ...link,
                disabled: link.isDisabled ? link.isDisabled(ability) : link.disabled,
            }));
    }, [navLinks, ability]);

    const notificationsCount = notifications.filter((notif) => !notif.seen).length;
    const numberLateDeclarations = getLateDeclarations(contributionList).length;
    const pendingJobsCount = getPendingImports({
        jobs,
        fakeImports: pendingImports,
        type: ["title", "beneficiar", "terms"],
    }).length;

    // Initialize Crisp chat
    useCrisp(portalType);

    useEffect(() => {
        if (authStatus === "unauthenticated") window.location.reload();
    }, [authStatus]);

    const handleLogOut = () => {
        dispatch({ type: "USER/LOGOUT" });
        signOut();
    };

    const onCloseNavDrawer = () => {
        setTimeout(() => dispatch(toggleNavDrawer(false)), 100);
    };

    const onOpenNotifications = () => {
        if (isMobile) {
            displayMobileNavBtn(false);
            dispatch(toggleNotificationsDrawer(true));
        } else {
            dispatch(toggleNotificationsDrawer(!onOpenNotificationsDrawer));
        }
    };

    const onCloseNotifications = () => {
        if (isMobile) displayMobileNavBtn(true);
        dispatch(toggleNotificationsDrawer(false));
    };

    const handlePendingJobs = async () => {
        navigate("/settings/history");
        await dispatch(fetchImportedFiles({ options: {} }));
    };

    // Create declaration link with chip
    const createDeclarationLink = (link: NavigationLinkConfig): NavigationLinkConfig => {
        if (link.to.includes("declarations")) {
            return {
                ...link,
                customComponent: (
                    <DeclarationsLink lateCount={numberLateDeclarations} isCollapsed={effectiveCollapsed} />
                ),
            };
        }
        return link;
    };

    const topContent = (
        <>
            <NavigationHeader isCollapsed={effectiveCollapsed} />
            <div className="navigation-main">
                {allowedLinks.map((link) => (
                    <NavigationLink
                        key={link.to}
                        config={createDeclarationLink(link)}
                        onClick={isMobile ? onCloseNavDrawer : undefined}
                        isCollapsed={effectiveCollapsed}
                    />
                ))}
            </div>
        </>
    );

    const bottomContent = (
        <>
            {bottomLinks.map((link) => (
                <NavigationLink
                    key={link.to}
                    config={link}
                    onClick={isMobile ? onCloseNavDrawer : undefined}
                    isCollapsed={effectiveCollapsed}
                />
            ))}

            <HelpMenu
                portalType={portalType as "press" | "audiovisual" | "urssaf"}
                hasNavLinks={navLinks.length > 0}
                onActionComplete={isMobile ? () => dispatch(toggleNavDrawer(false)) : undefined}
                isCollapsed={effectiveCollapsed}
            />

            {/* Notifications and pending imports are not shown for URSSAF */}
            {portalType !== EPortalType.URSSAF && (
                <>
                    <NotificationsButton
                        unreadCount={notificationsCount}
                        onClick={onOpenNotifications}
                        isCollapsed={effectiveCollapsed}
                    />

                    {[EPortalType.PRESS, EPortalType.AUDIOVISUAL].includes(portalType as EPortalType) && (
                        <PendingImportsButton
                            pendingCount={pendingJobsCount}
                            onClick={handlePendingJobs}
                            isOnHistoryPage={window.location.href.includes("settings/history")}
                            isCollapsed={effectiveCollapsed}
                        />
                    )}
                </>
            )}

            {rolePreview && isCrealoAdmin && <RolePreview role={rolePreview.role} label={rolePreview.label} />}

            <NavigationProfile
                userName={user.attributes?.name}
                companyName={companySettings?.name}
                avatarUrl={companySettings?.avatarUrl}
                portalType={portalType as "press" | "audiovisual"}
                showCompanySwitcher={showCompanySelector}
                companies={userData?.companies ?? []}
                currentCompanyId={companySettings?.id}
                onSwitchCompany={handleSwitchCompany}
                onLogout={handleLogOut}
                isCollapsed={effectiveCollapsed}
            />
        </>
    );

    return (
        <>
            <NavigationLayout
                variant="publisher"
                isMobile={isMobile}
                isOpen={onOpenNavDrawer}
                isCollapsed={effectiveCollapsed}
                isPWAMode={isPWAMode}
                topContent={topContent}
                bottomContent={bottomContent}
                collapseButton={!isMobile ? <CollapseButton /> : undefined}
            />

            <Drawer
                anchor="bottom"
                open={onOpenNotificationsDrawer}
                className={classNames("notification-wrapper", { "is-PWA": isPWAMode })}
                onClick={onCloseNotifications}
            >
                <NotificationCenter close={onCloseNotifications} />
            </Drawer>
        </>
    );
};

/**
 * Navigation component for Publisher portals (Press & Audiovisual & URSSAF) with collapse support
 */
const PublisherNavigation = (props: PublisherNavigationProps) => {
    return (
        <NavigationProvider>
            <PublisherNavigationContent {...props} />
        </NavigationProvider>
    );
};

export default PublisherNavigation;
